import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdWarning, MdSchedule, MdRefresh, MdSettings, MdClose, MdPerson, MdOpenInNew, MdPayment, MdPhone, MdSearch } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { recibosAPI, whatsappAPI, clientesAPI } from '../services/api';
import { SkeletonAlertCard, EmptyState } from '../components/SkeletonLoader';

const WHATSAPP_GREEN = '#25D366';

// ── Styled components ────────────────────────────────────────────────────────

const AlertsContainer = styled.div`max-width: 1400px;`;

const PageHeader = styled.div`margin-bottom: ${p => p.theme.spacing.xl};`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-top: ${p => p.theme.spacing.lg};
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.medium};
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background-color: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: ${p => p.theme.fontWeights.medium};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  &:hover { border-color: ${p => p.theme.colors.primary}; }
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; box-shadow: 0 0 0 2px ${p => p.theme.colors.primary}20; }
`;

const NumberInput = styled.input`
  width: 80px;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background-color: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: ${p => p.theme.fontWeights.medium};
  text-align: center;
  transition: ${p => p.theme.transitions.fast};
  &:hover { border-color: ${p => p.theme.colors.primary}; }
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; box-shadow: 0 0 0 2px ${p => p.theme.colors.primary}20; }
`;

const TabButton = styled.button`
  padding: 7px 18px;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.active ? 600 : 500};
  font-family: ${p => p.theme.fonts.primary};
  transition: ${p => p.theme.transitions.fast};
  cursor: pointer;
  box-shadow: ${p => p.active ? `0 4px 12px ${p.theme.colors.primary}35` : 'none'};
  &:hover {
    background: ${p => p.active
      ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : p.theme.colors.border};
    color: ${p => p.active ? 'white' : p.theme.colors.textPrimary};
  }
`;

const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(350px, 100%), 1fr));
  gap: ${p => p.theme.spacing.lg};
`;

const AlertCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  border-left: 4px solid ${p => p.cardType === 'overdue' ? p.theme.colors.danger : p.theme.colors.warning};
  transition: ${p => p.theme.transitions.fast};
  &:hover { box-shadow: ${p => p.theme.shadows.md}; transform: translateY(-2px); }
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.type === 'overdue' ? p.theme.colors.danger + '20' : p.theme.colors.warning + '20'};
  color: ${p => p.type === 'overdue' ? p.theme.colors.danger : p.theme.colors.warning};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: 1.5rem;
`;

const AlertBadge = styled.div`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.sm};
  background-color: ${p => p.type === 'overdue' ? p.theme.colors.danger : p.theme.colors.warning};
  color: white;
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const ClientName = styled.h3`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 4px 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const ClientPhone = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: ${p => p.theme.spacing.md};
  svg { font-size: 0.9rem; }
`;

const Amount = styled.div`
  font-size: ${p => p.theme.fontSizes.xxl};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.bold};
  margin-bottom: 2px;
`;

const AmountSubtext = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  margin-bottom: ${p => p.theme.spacing.md};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ReceiptsList = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.sm};
  margin-bottom: ${p => p.theme.spacing.md};
  overflow: hidden;
`;

const ReceiptsListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.backgroundDark};
`;

const ReceiptsListMeta = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 500;
`;

const ToggleAllBtn = styled.button`
  background: none;
  border: none;
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.primary};
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: ${p => p.theme.fonts.primary};
  &:hover { text-decoration: underline; }
`;

const ReceiptListItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.colors.backgroundDark}; }
`;

const ReceiptCheckbox = styled.input`
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: ${p => p.theme.colors.primary};
  flex-shrink: 0;
`;

const ReceiptListLeft = styled.div`flex: 1;`;

const ReceiptListNum = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const ReceiptListDays = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.warning ? p.theme.colors.warning : p.theme.colors.danger};
`;

const ReceiptListAmount = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 700;
  color: ${p => p.theme.colors.danger};
  flex-shrink: 0;
`;

const ReceiptListNav = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.textLight};
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 1rem;
  transition: ${p => p.theme.transitions.fast};
  &:hover {
    color: ${p => p.pay ? p.theme.colors.success : p.theme.colors.primary};
    background: ${p => p.theme.colors.border};
  }
`;

const SectionDivider = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin: ${p => p.theme.spacing.sm} 0;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${p => p.theme.colors.border};
  }
`;

const SectionDividerLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: ${p => p.theme.spacing.md};
  background-color: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  transition: ${p => p.theme.transitions.fast};
  cursor: pointer;
  &:hover { background-color: ${p => p.theme.colors.background}; border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const NotificationButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const WhatsAppButton = styled.button`
  flex: 1;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background-color: ${WHATSAPP_GREEN};
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  transition: ${p => p.theme.transitions.fast};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.xs};
  svg { font-size: 1.1rem; }
  &:hover { opacity: 0.9; transform: translateY(-2px); box-shadow: ${p => p.theme.shadows.md}; }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const ConfigButton = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background-color: ${p => p.active ? p.theme.colors.primary + '20' : p.theme.colors.backgroundLight};
  color: ${p => p.active ? p.theme.colors.primary : p.theme.colors.textSecondary};
  border: 1px solid ${p => p.active ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: ${p => p.theme.transitions.fast};
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
  svg { font-size: 1rem; }
`;

const BulkActionsBar = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.lg};
  margin-top: ${p => p.theme.spacing.md};
  margin-bottom: 2px;
  flex-wrap: wrap;
`;

const BulkGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 12px 16px;
  min-width: 220px;
  flex: 1;
`;

const BulkGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.overdue ? p.theme.colors.danger : p.theme.colors.warning};
  margin-bottom: 4px;
  svg { font-size: 0.85rem; }
`;

const BulkGroupMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  margin-bottom: 6px;
`;

const BulkGroupButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const BulkBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  border: none;

  ${p => p.whatsapp && `
    background: ${WHATSAPP_GREEN};
    color: white;
    &:hover { opacity: 0.88; box-shadow: 0 4px 10px rgba(37,211,102,0.3); }
  `}
  ${p => p.auto && `
    background: ${p.theme.colors.backgroundDark};
    color: ${p.theme.colors.textSecondary};
    border: 1px solid ${p.theme.colors.border};
    &:hover { border-color: ${p.theme.colors.primary}; color: ${p.theme.colors.primary}; }
  `}
  &:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
  svg { font-size: 1rem; flex-shrink: 0; }
`;

// ── Modal de configuración automática ────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  width: 420px;
  max-width: 95vw;
  box-shadow: ${p => p.theme.shadows.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  font-weight: ${p => p.theme.fontWeights.semibold};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  &:hover { color: ${p => p.theme.colors.textPrimary}; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.theme.fontWeights.medium};
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Toggle = styled.input`
  width: 42px;
  height: 24px;
  cursor: pointer;
  accent-color: ${p => p.theme.colors.primary};
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: ${p => p.theme.spacing.md};
  background-color: ${WHATSAPP_GREEN};
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.sm};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const WideModal = styled(Modal)`
  width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
`;

const BulkControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: 10px 12px;
  background: ${p => p.theme.colors.backgroundDark};
  border-radius: ${p => p.theme.borderRadius.md};
  margin-bottom: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const BulkControlLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 4px;
`;

const SmallBtn = styled.button`
  padding: 4px 12px;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textSecondary};
  transition: ${p => p.theme.transitions.fast};
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const FreqApplyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const SmallNumberInput = styled.input`
  width: 52px;
  padding: 4px 6px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.xs};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  text-align: center;
  font-family: ${p => p.theme.fonts.primary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const ClientsList = styled.div`
  flex: 1;
  overflow-y: auto;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  margin-bottom: ${p => p.theme.spacing.md};
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme.colors.border}; border-radius: 4px; }
`;

const ClientConfigRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  &:last-child { border-bottom: none; }
  background: ${p => p.loading ? p.theme.colors.backgroundDark : 'transparent'};
  transition: background 0.15s;
  &:hover { background: ${p => p.theme.colors.backgroundDark}; }
`;

const ClientConfigToggle = styled.input`
  width: 36px;
  height: 20px;
  cursor: pointer;
  accent-color: ${p => p.theme.colors.primary};
  flex-shrink: 0;
`;

const ClientConfigName = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClientConfigNameText = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 500;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClientConfigPhone = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.valid ? p.theme.colors.textLight : p.theme.colors.warning};
  display: flex;
  align-items: center;
  gap: 3px;
  svg { font-size: 0.75rem; }
`;

const ClientConfigFreqRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ClientConfigFreqInput = styled.input`
  width: 48px;
  padding: 4px 6px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.xs};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  text-align: center;
  font-family: ${p => p.theme.fonts.primary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &:disabled { opacity: 0.4; }
`;

const ClientConfigFreqLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  padding-top: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const PhoneUpdateBlock = styled.div`
  margin-bottom: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.warning}15;
  border: 1px solid ${p => p.theme.colors.warning}40;
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
`;

const PhoneWarningText = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.warning};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: ${p => p.theme.spacing.sm};
  svg { font-size: 0.9rem; }
`;

const PhoneEditRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const PhoneInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const PhoneSaveBtn = styled.button`
  padding: 6px 14px;
  background: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  white-space: nowrap;
  transition: ${p => p.theme.transitions.fast};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const LoadingCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 40px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  box-shadow: ${p => p.theme.shadows.lg};
  min-width: 300px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${p => p.theme.colors.border};
  border-top-color: ${WHATSAPP_GREEN};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const LoadingTitle = styled.div`
  font-size: ${p => p.theme.fontSizes.lg};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.textPrimary};
`;

const LoadingProgress = styled.div`
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textSecondary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${p => p.theme.colors.border};
  border-radius: 99px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${WHATSAPP_GREEN};
  border-radius: 99px;
  transition: width 0.3s ease;
  width: ${p => p.pct}%;
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: 10px;
  color: ${p => p.theme.colors.textLight};
  font-size: 1.1rem;
  pointer-events: none;
`;

const SearchInput = styled.input`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  padding-left: 32px;
  background-color: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.md};
  font-family: ${p => p.theme.fonts.primary};
  width: clamp(140px, 200px, 100%);
  transition: ${p => p.theme.transitions.fast};
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
  &:hover { border-color: ${p => p.theme.colors.primary}; }
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; box-shadow: 0 0 0 2px ${p => p.theme.colors.primary}20; }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 20px;
  background: ${p => p.error ? '#f8d7da' : '#d4edda'};
  color: ${p => p.error ? '#721c24' : '#155724'};
  border: 1px solid ${p => p.error ? '#f5c6cb' : '#c3e6cb'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 2000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 380px;
`;

// ── Componente principal ─────────────────────────────────────────────────────

const Alerts = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Todas');
  const [filterNumber, setFilterNumber] = useState(7);
  const [filterUnit, setFilterUnit] = useState('días');
  const [searchQuery, setSearchQuery] = useState('');
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sendingWA, setSendingWA] = useState({});
  const [toast, setToast] = useState(null);
  const [selectedRecibos, setSelectedRecibos] = useState({});
  const [selectedProximos, setSelectedProximos] = useState({});

  const [editingPhone, setEditingPhone] = useState({});
  const [savingPhone, setSavingPhone] = useState({});
  const [sendingAll, setSendingAll] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0 });
  const [autoTodosModal, setAutoTodosModal] = useState(null); // 'vencido' | 'proximo' | null
  const [clientsAutoConfig, setClientsAutoConfig] = useState({});
  const [loadingAutoConfigs, setLoadingAutoConfigs] = useState(false);
  const [savingAutoTodos, setSavingAutoTodos] = useState(false);
  const [globalFreq, setGlobalFreq] = useState(7);

  // Modal de configuración automática
  const [configModal, setConfigModal] = useState(null); // { clienteId, clienteNombre }
  const [configData, setConfigData] = useState({ activo: false, frecuencia_dias: 7 });
  const [savingConfig, setSavingConfig] = useState(false);
  // Caché de configs por clienteId para mostrar el indicador de activo en el botón
  const [configCache, setConfigCache] = useState({});

  useEffect(() => { fetchRecibos(); }, []);

  useEffect(() => {
    if (!recibos.length) return;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const initVencidos = {};
    const initProximos = {};
    recibos.forEach(r => {
      const fecha = new Date(r.fecha_vencimiento); fecha.setHours(0, 0, 0, 0);
      const dias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));
      if (dias < 0) {
        if (!initVencidos[r.cliente_id]) initVencidos[r.cliente_id] = new Set();
        initVencidos[r.cliente_id].add(r.id);
      } else {
        if (!initProximos[r.cliente_id]) initProximos[r.cliente_id] = new Set();
        initProximos[r.cliente_id].add(r.id);
      }
    });
    setSelectedRecibos(initVencidos);
    setSelectedProximos(initProximos);
  }, [recibos]);

  const fetchRecibos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await recibosAPI.getAll();
      const todos = response.data.data || [];
      setRecibos(todos.filter(r =>
        parseFloat(r.saldo_pendiente || 0) > 0 &&
        r.estado !== 'pagado' &&
        r.estado !== 'cancelado'
      ));
    } catch (err) {
      setError(err.message || 'Error al cargar recibos');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, error = false) => {
    setToast({ message, error });
    setTimeout(() => setToast(null), 4000);
  };

  const calcularDias = (fechaVencimiento) => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const fecha = new Date(fechaVencimiento); fecha.setHours(0, 0, 0, 0);
    return Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));
  };

  const alertas = recibos.map(r => ({
    id: r.id,
    recibo_id: r.id,
    numero_recibo: r.numero_recibo,
    cliente_nombre: r.cliente_nombre,
    cliente_telefono: r.cliente_telefono,
    cliente_id: r.cliente_id,
    // Si el recibo es de un sub-cliente, la cobranza se dirige al padre (quien
    // realmente cobra); si no, es el propio cliente. `subcliente_nombre` se
    // conserva para distinguir de dónde viene cada recibo dentro de la tarjeta.
    cobro_cliente_id: r.cliente_padre_id || r.cliente_id,
    cobro_cliente_nombre: r.cliente_padre_id ? r.cliente_padre_nombre : r.cliente_nombre,
    cobro_cliente_telefono: r.cliente_padre_id ? r.cliente_padre_telefono : r.cliente_telefono,
    subcliente_nombre: r.cliente_padre_id ? r.cliente_nombre : null,
    monto_total: r.monto_total,
    saldo_pendiente: r.saldo_pendiente,
    fecha_vencimiento: r.fecha_vencimiento,
    recibo_estado: r.estado,
    dias: calcularDias(r.fecha_vencimiento),
    tipo: calcularDias(r.fecha_vencimiento) < 0 ? 'vencido' : 'proximo'
  }));

  // ── Agrupación de vencidos por cliente (o por su cliente padre, si el
  // recibo es de un sub-cliente) ──────────────────────────────────────────

  const groupByClient = (alertas, ascending = false) => {
    const map = {};
    alertas.forEach(a => {
      if (!map[a.cobro_cliente_id]) {
        map[a.cobro_cliente_id] = {
          cliente_id: a.cobro_cliente_id,
          cliente_nombre: a.cobro_cliente_nombre,
          cliente_telefono: a.cobro_cliente_telefono,
          recibos: [],
        };
      }
      map[a.cobro_cliente_id].recibos.push(a);
    });

    return Object.values(map).map(g => {
      g.recibos.sort((a, b) => a.dias - b.dias);
      return {
        ...g,
        total_saldo: g.recibos.reduce((s, r) => s + parseFloat(r.saldo_pendiente || 0), 0),
        max_dias: Math.abs(g.recibos[0].dias),
        worst_recibo: g.recibos[0],
      };
    }).sort((a, b) => ascending ? a.max_dias - b.max_dias : b.max_dias - a.max_dias);
  };

  // ── Selección de recibos en tarjetas agrupadas ──────────────────────────────

  const toggleRecibo = (clienteId, reciboId) => {
    setSelectedRecibos(prev => {
      const current = new Set(prev[clienteId] || []);
      if (current.has(reciboId)) current.delete(reciboId);
      else current.add(reciboId);
      return { ...prev, [clienteId]: current };
    });
  };

  const toggleAllRecibos = (grupo) => {
    setSelectedRecibos(prev => {
      const current = prev[grupo.cliente_id] || new Set();
      const allSelected = grupo.recibos.every(r => current.has(r.recibo_id));
      return {
        ...prev,
        [grupo.cliente_id]: allSelected
          ? new Set()
          : new Set(grupo.recibos.map(r => r.recibo_id)),
      };
    });
  };

  const toggleReciboProximo = (clienteId, reciboId) => {
    setSelectedProximos(prev => {
      const current = new Set(prev[clienteId] || []);
      if (current.has(reciboId)) current.delete(reciboId);
      else current.add(reciboId);
      return { ...prev, [clienteId]: current };
    });
  };

  const toggleAllProximos = (grupo) => {
    setSelectedProximos(prev => {
      const current = prev[grupo.cliente_id] || new Set();
      const allSelected = grupo.recibos.every(r => current.has(r.recibo_id));
      return {
        ...prev,
        [grupo.cliente_id]: allSelected
          ? new Set()
          : new Set(grupo.recibos.map(r => r.recibo_id)),
      };
    });
  };

  const handleSendWhatsAppProximos = async (grupo) => {
    const sel = selectedProximos[grupo.cliente_id];
    const reciboIds = grupo.recibos
      .filter(r => !sel || sel.has(r.recibo_id))
      .map(r => r.recibo_id);

    if (reciboIds.length === 0) {
      showToast('Selecciona al menos un recibo para enviar', true);
      return;
    }

    setSendingWA(prev => ({ ...prev, [`p_${grupo.cliente_id}`]: true }));
    try {
      const res = await whatsappAPI.enviarCombinado(reciboIds);
      showToast(res.data.message || `Mensaje enviado a ${grupo.cliente_nombre}`);
    } catch (err) {
      showToast(err.message || 'Error al enviar el mensaje de WhatsApp', true);
    } finally {
      setSendingWA(prev => ({ ...prev, [`p_${grupo.cliente_id}`]: false }));
    }
  };

  // ── Envío manual de WhatsApp ────────────────────────────────────────────────

  const handleSendWhatsApp = async (alert) => {
    setSendingWA(prev => ({ ...prev, [alert.id]: true }));
    try {
      const res = await whatsappAPI.enviar(alert.recibo_id);
      showToast(res.data.message || `Mensaje enviado a ${alert.cliente_nombre}`);
    } catch (err) {
      const msg = err.message || 'Error al enviar el mensaje de WhatsApp';
      showToast(msg, true);
    } finally {
      setSendingWA(prev => ({ ...prev, [alert.id]: false }));
    }
  };

  const handleSendWhatsAppGrupo = async (grupo) => {
    const sel = selectedRecibos[grupo.cliente_id];
    const reciboIds = grupo.recibos
      .filter(r => !sel || sel.has(r.recibo_id))
      .map(r => r.recibo_id);

    if (reciboIds.length === 0) {
      showToast('Selecciona al menos un recibo para enviar', true);
      return;
    }

    setSendingWA(prev => ({ ...prev, [grupo.cliente_id]: true }));
    try {
      const res = await whatsappAPI.enviarCombinado(reciboIds);
      showToast(res.data.message || `Mensaje enviado a ${grupo.cliente_nombre}`);
    } catch (err) {
      showToast(err.message || 'Error al enviar el mensaje de WhatsApp', true);
    } finally {
      setSendingWA(prev => ({ ...prev, [grupo.cliente_id]: false }));
    }
  };

  // ── Modal de configuración automática ──────────────────────────────────────

  const openConfigModal = useCallback(async (alert) => {
    const tipo = alert.tipo || 'vencido';
    setConfigModal({ clienteId: alert.cliente_id, clienteNombre: alert.cliente_nombre, tipo });
    try {
      const res = await whatsappAPI.getConfigCliente(alert.cliente_id, tipo);
      const data = res.data.data;
      setConfigData({ activo: !!data.activo, frecuencia_dias: data.frecuencia_dias || 7 });
    } catch {
      setConfigData({ activo: false, frecuencia_dias: 7 });
    }
  }, []);

  const handleSaveConfig = async () => {
    if (!configModal) return;
    setSavingConfig(true);
    try {
      await whatsappAPI.updateConfigCliente(configModal.clienteId, { ...configData, tipo: configModal.tipo });
      setConfigCache(prev => ({ ...prev, [`${configModal.clienteId}_${configModal.tipo}`]: configData.activo }));
      showToast(`Configuración guardada para ${configModal.clienteNombre}`);
      setConfigModal(null);
    } catch (err) {
      showToast(err.message || 'Error al guardar la configuración', true);
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Teléfono faltante / inválido ────────────────────────────────────────────

  const phoneStatus = (telefono) => {
    if (!telefono || !telefono.trim()) return 'missing';
    const digits = telefono.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) return 'invalid';
    // Con código de país mexicano debe ser exactamente 12 dígitos
    if (digits.startsWith('52') && digits.length !== 12) return 'invalid';
    return 'valid';
  };

  const isPhoneValid = (telefono) => phoneStatus(telefono) === 'valid';

  const handleSavePhone = async (clienteId) => {
    const valor = (editingPhone[clienteId] || '').trim();
    if (!valor) return;
    setSavingPhone(prev => ({ ...prev, [clienteId]: true }));
    try {
      await clientesAPI.update(clienteId, { telefono: valor });
      showToast('Teléfono actualizado correctamente');
      fetchRecibos();
    } catch {
      showToast('Error al actualizar el teléfono', true);
    } finally {
      setSavingPhone(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  const handleEnviarTodos = async (grupos, selMap) => {
    const validos = grupos.filter(g => isPhoneValid(g.cliente_telefono));
    if (validos.length === 0) { showToast('Ningún cliente tiene teléfono válido', true); return; }
    setSendingAll(true);
    setSendingProgress({ current: 0, total: validos.length });
    let ok = 0; let err = 0;
    for (let i = 0; i < validos.length; i++) {
      const grupo = validos[i];
      setSendingProgress({ current: i + 1, total: validos.length });
      const sel = selMap[grupo.cliente_id];
      const ids = grupo.recibos.filter(r => !sel || sel.has(r.recibo_id)).map(r => r.recibo_id);
      if (ids.length === 0) continue;
      try {
        await whatsappAPI.enviarCombinado(ids);
        ok++;
      } catch { err++; }
      await new Promise(r => setTimeout(r, 300));
    }
    setSendingAll(false);
    setSendingProgress({ current: 0, total: 0 });
    showToast(`${ok} mensaje${ok !== 1 ? 's' : ''} enviado${ok !== 1 ? 's' : ''}${err > 0 ? `, ${err} con error` : ''}`, err > 0 && ok === 0);
  };

  const openAutoTodosModal = async (tipo, grupos) => {
    setAutoTodosModal(tipo);
    setLoadingAutoConfigs(true);
    const configs = {};
    await Promise.all(grupos.map(async (g) => {
      try {
        const res = await whatsappAPI.getConfigCliente(g.cliente_id, tipo);
        const d = res.data.data;
        configs[g.cliente_id] = { activo: !!d.activo, frecuencia_dias: d.frecuencia_dias || 7 };
      } catch {
        configs[g.cliente_id] = { activo: false, frecuencia_dias: 7 };
      }
    }));
    setClientsAutoConfig(configs);
    setLoadingAutoConfigs(false);
  };

  const updateClientConfig = (clienteId, field, value) => {
    setClientsAutoConfig(prev => ({
      ...prev,
      [clienteId]: { ...prev[clienteId], [field]: value }
    }));
  };

  const toggleAllAuto = (activo) => {
    setClientsAutoConfig(prev => {
      const updated = {};
      Object.keys(prev).forEach(id => { updated[id] = { ...prev[id], activo }; });
      return updated;
    });
  };

  const applyFreqAll = () => {
    setClientsAutoConfig(prev => {
      const updated = {};
      Object.keys(prev).forEach(id => { updated[id] = { ...prev[id], frecuencia_dias: globalFreq }; });
      return updated;
    });
  };

  const handleSaveAutoTodos = async () => {
    setSavingAutoTodos(true);
    let ok = 0;
    for (const g of Object.keys(clientsAutoConfig).map(id => ({ cliente_id: parseInt(id) }))) {
      const cfg = clientsAutoConfig[g.cliente_id];
      if (!cfg) continue;
      try {
        await whatsappAPI.updateConfigCliente(g.cliente_id, { ...cfg, tipo: autoTodosModal });
        setConfigCache(prev => ({ ...prev, [`${g.cliente_id}_${autoTodosModal}`]: cfg.activo }));
        ok++;
      } catch { /* continuar */ }
    }
    setSavingAutoTodos(false);
    setAutoTodosModal(null);
    showToast(`Auto guardado para ${ok} cliente${ok !== 1 ? 's' : ''}`);
  };

  // ── Filtros ─────────────────────────────────────────────────────────────────

  const getDays = () => {
    const n = parseInt(filterNumber) || 0;
    if (filterUnit === 'meses') return n * 30;
    if (filterUnit === 'años') return n * 365;
    return n;
  };

  const vencidosAll = alertas.filter(a => a.tipo === 'vencido');
  const stats = {
    total: alertas.length,
    vencidos: [...new Set(vencidosAll.map(a => a.cliente_id))].length,
    proximos: alertas.filter(a => a.tipo === 'proximo').length,
  };

  const filtered = alertas.filter(a => {
    if (activeTab === 'Todas') return true;
    const days = getDays();
    if (activeTab === 'Vencidos') return a.tipo === 'vencido' && Math.abs(a.dias) <= days;
    if (activeTab === 'Proximos') return a.tipo === 'proximo' && a.dias >= 0 && a.dias <= days;
    return true;
  }).sort((a, b) => {
    if (a.tipo === 'vencido' && b.tipo === 'vencido') return a.dias - b.dias;
    if (a.tipo === 'vencido') return -1;
    if (b.tipo === 'vencido') return 1;
    return a.dias - b.dias;
  });

  const filteredVencidos = filtered.filter(a => a.tipo === 'vencido');
  const filteredProximos = filtered.filter(a => a.tipo === 'proximo');
  const clientesVencidos = groupByClient(filteredVencidos)
    .filter(g => !searchQuery || g.cliente_nombre?.toLowerCase().includes(searchQuery.toLowerCase()));
  const clientesProximos = groupByClient(filteredProximos, true)
    .filter(g => !searchQuery || g.cliente_nombre?.toLowerCase().includes(searchQuery.toLowerCase()));

  const hayAmbos = clientesVencidos.length > 0 && clientesProximos.length > 0;

  return (
    <AlertsContainer>
      <PageHeader>
        <Title>Cobranza</Title>
        <FilterSection>
          <SearchWrapper>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchWrapper>
          <TabButton active={activeTab === 'Todas'} onClick={() => setActiveTab('Todas')}>
            Todas ({stats.total})
          </TabButton>
          <TabButton active={activeTab === 'Vencidos'} onClick={() => setActiveTab('Vencidos')}>
            Vencidos ({stats.vencidos} {stats.vencidos === 1 ? 'cliente' : 'clientes'})
          </TabButton>
          <TabButton active={activeTab === 'Proximos'} onClick={() => setActiveTab('Proximos')}>
            Próximos ({stats.proximos})
          </TabButton>

          {(activeTab === 'Vencidos' || activeTab === 'Proximos') && (
            <FilterGroup>
              <FilterLabel>Mostrar hasta:</FilterLabel>
              <NumberInput type="number" min="1" max="999" value={filterNumber} onChange={e => setFilterNumber(e.target.value)} />
              <Select value={filterUnit} onChange={e => setFilterUnit(e.target.value)}>
                <option value="días">Días</option>
                <option value="meses">Meses</option>
                <option value="años">Años</option>
              </Select>
              <FilterLabel>{activeTab === 'Vencidos' ? 'vencido' : 'para vencimiento'}</FilterLabel>
            </FilterGroup>
          )}

          <TabButton onClick={fetchRecibos} disabled={loading}>
            <MdRefresh style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Actualizar
          </TabButton>
        </FilterSection>
      </PageHeader>

      {!loading && filtered.length > 0 && (
        <BulkActionsBar>
          {(activeTab === 'Todas' || activeTab === 'Vencidos') && clientesVencidos.length > 0 && (() => {
            const conTel = clientesVencidos.filter(g => isPhoneValid(g.cliente_telefono)).length;
            return (
              <BulkGroup>
                <BulkGroupHeader overdue>
                  <MdWarning /> Vencidos
                </BulkGroupHeader>
                <BulkGroupMeta>
                  {clientesVencidos.length} cliente{clientesVencidos.length !== 1 ? 's' : ''} •{' '}
                  {conTel} con WhatsApp disponible
                </BulkGroupMeta>
                <BulkGroupButtons>
                  <BulkBtn whatsapp disabled={sendingAll || conTel === 0}
                    onClick={() => handleEnviarTodos(clientesVencidos, selectedRecibos)}
                    title={conTel === 0 ? 'Ningún cliente tiene teléfono válido' : `Enviar a ${conTel} cliente(s)`}
                  >
                    <FaWhatsapp />
                    {sendingAll ? 'Enviando...' : 'Enviar a todos'}
                  </BulkBtn>
                  <BulkBtn auto
                    onClick={() => openAutoTodosModal('vencido', clientesVencidos)}
                    title="Configurar mensajes automáticos para todos los clientes con vencidos"
                  >
                    <MdSettings />
                    Auto
                  </BulkBtn>
                </BulkGroupButtons>
              </BulkGroup>
            );
          })()}

          {(activeTab === 'Todas' || activeTab === 'Proximos') && clientesProximos.length > 0 && (() => {
            const conTel = clientesProximos.filter(g => isPhoneValid(g.cliente_telefono)).length;
            return (
              <BulkGroup>
                <BulkGroupHeader>
                  <MdSchedule /> Próximos a vencer
                </BulkGroupHeader>
                <BulkGroupMeta>
                  {clientesProximos.length} cliente{clientesProximos.length !== 1 ? 's' : ''} •{' '}
                  {conTel} con WhatsApp disponible
                </BulkGroupMeta>
                <BulkGroupButtons>
                  <BulkBtn whatsapp disabled={sendingAll || conTel === 0}
                    onClick={() => handleEnviarTodos(clientesProximos, selectedProximos)}
                    title={conTel === 0 ? 'Ningún cliente tiene teléfono válido' : `Enviar a ${conTel} cliente(s)`}
                  >
                    <FaWhatsapp />
                    {sendingAll ? 'Enviando...' : 'Enviar a todos'}
                  </BulkBtn>
                  <BulkBtn auto
                    onClick={() => openAutoTodosModal('proximo', clientesProximos)}
                    title="Configurar mensajes automáticos para todos los clientes con próximos"
                  >
                    <MdSettings />
                    Auto
                  </BulkBtn>
                </BulkGroupButtons>
              </BulkGroup>
            );
          })()}
        </BulkActionsBar>
      )}

      {error && (
        <div style={{ padding: '20px', backgroundColor: '#fee', color: '#c33', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <AlertsGrid>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonAlertCard key={i} />)}
        </AlertsGrid>
      ) : filtered.length === 0 ? (
        <EmptyState
          type="alerts"
          title={activeTab === 'Todas' ? '¡Sin alertas pendientes!' : activeTab === 'Vencidos' ? 'Sin vencidos' : 'Sin próximos a vencer'}
          subtitle={activeTab === 'Todas' ? 'Todos los recibos están al corriente.' : 'No hay recibos en este rango de tiempo.'}
        />
      ) : (
        <AlertsGrid>
          {/* ── Vencidos agrupados por cliente ── */}
          {clientesVencidos.length > 0 && hayAmbos && (
            <SectionDivider>
              <SectionDividerLabel>Vencidos — {clientesVencidos.length} {clientesVencidos.length === 1 ? 'cliente' : 'clientes'}</SectionDividerLabel>
            </SectionDivider>
          )}

          {clientesVencidos.map(grupo => {
            const sel = selectedRecibos[grupo.cliente_id] ?? new Set(grupo.recibos.map(r => r.recibo_id));
            const numSel = grupo.recibos.filter(r => sel.has(r.recibo_id)).length;
            const allSel = numSel === grupo.recibos.length;

            return (
            <AlertCard key={`grupo-${grupo.cliente_id}`} cardType="overdue">
              <AlertHeader>
                <AlertIcon type="overdue"><MdWarning /></AlertIcon>
                <AlertBadge type="overdue">
                  {grupo.recibos.length} recibo{grupo.recibos.length !== 1 ? 's' : ''} vencido{grupo.recibos.length !== 1 ? 's' : ''}
                </AlertBadge>
              </AlertHeader>

              <ClientName>{grupo.cliente_nombre || 'Sin nombre'}</ClientName>
              {grupo.cliente_telefono && (
                <ClientPhone><MdPhone />{grupo.cliente_telefono}</ClientPhone>
              )}

              <Amount>
                ${grupo.total_saldo.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Amount>
              <AmountSubtext>saldo total vencido</AmountSubtext>

              <ReceiptsList>
                <ReceiptsListHeader>
                  <ReceiptsListMeta>
                    {numSel} de {grupo.recibos.length} seleccionado{numSel !== 1 ? 's' : ''}
                  </ReceiptsListMeta>
                  <ToggleAllBtn onClick={() => toggleAllRecibos(grupo)}>
                    {allSel ? 'Quitar todos' : 'Seleccionar todos'}
                  </ToggleAllBtn>
                </ReceiptsListHeader>
                {grupo.recibos.map(r => (
                  <ReceiptListItem key={r.id} onClick={() => toggleRecibo(grupo.cliente_id, r.recibo_id)}>
                    <ReceiptCheckbox
                      type="checkbox"
                      checked={sel.has(r.recibo_id)}
                      onChange={() => toggleRecibo(grupo.cliente_id, r.recibo_id)}
                      onClick={e => e.stopPropagation()}
                    />
                    <ReceiptListLeft>
                      <ReceiptListNum>
                        {r.subcliente_nombre && `${r.subcliente_nombre} · `}Recibo #{r.numero_recibo}
                      </ReceiptListNum>
                      <ReceiptListDays>
                        {Math.abs(r.dias)} día{Math.abs(r.dias) !== 1 ? 's' : ''} vencido
                      </ReceiptListDays>
                    </ReceiptListLeft>
                    <ReceiptListAmount>
                      ${parseFloat(r.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </ReceiptListAmount>
                    <ReceiptListNav
                      onClick={e => { e.stopPropagation(); navigate('/registrar-pago', { state: { reciboId: r.recibo_id, clienteId: grupo.cliente_id } }); }}
                      title="Registrar pago"
                      pay
                    >
                      <MdPayment />
                    </ReceiptListNav>
                    <ReceiptListNav
                      onClick={e => { e.stopPropagation(); navigate(`/recibos/${r.recibo_id}`); }}
                      title="Ver recibo"
                    >
                      <MdOpenInNew />
                    </ReceiptListNav>
                  </ReceiptListItem>
                ))}
              </ReceiptsList>

              {isPhoneValid(grupo.cliente_telefono) ? (
                <NotificationButtons>
                  <WhatsAppButton
                    onClick={() => handleSendWhatsAppGrupo(grupo)}
                    disabled={sendingWA[grupo.cliente_id] || numSel === 0}
                    title="Enviar recordatorio de cobro por WhatsApp"
                  >
                    <FaWhatsapp />
                    {sendingWA[grupo.cliente_id]
                      ? 'Enviando...'
                      : numSel < grupo.recibos.length
                        ? `Enviar WhatsApp (${numSel})`
                        : 'Enviar WhatsApp'
                    }
                  </WhatsAppButton>
                  <ConfigButton
                    active={configCache[`${grupo.cliente_id}_vencido`] === true}
                    onClick={() => openConfigModal({ cliente_id: grupo.cliente_id, cliente_nombre: grupo.cliente_nombre, tipo: 'vencido' })}
                    title="Configurar mensajes automáticos para recibos vencidos"
                  >
                    <MdSettings />
                    Auto
                  </ConfigButton>
                </NotificationButtons>
              ) : (
                <PhoneUpdateBlock>
                  <PhoneWarningText>
                    <MdPhone />
                    {phoneStatus(grupo.cliente_telefono) === 'missing'
                      ? 'Sin teléfono registrado'
                      : `Teléfono inválido: "${grupo.cliente_telefono}"`}
                  </PhoneWarningText>
                  <PhoneEditRow>
                    <PhoneInput
                      type="tel"
                      placeholder="Ej. 6641234567"
                      value={editingPhone[grupo.cliente_id] ?? (grupo.cliente_telefono || '')}
                      onChange={e => setEditingPhone(prev => ({ ...prev, [grupo.cliente_id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handleSavePhone(grupo.cliente_id)}
                    />
                    <PhoneSaveBtn
                      onClick={() => handleSavePhone(grupo.cliente_id)}
                      disabled={savingPhone[grupo.cliente_id]}
                    >
                      {savingPhone[grupo.cliente_id] ? 'Guardando...' : 'Guardar'}
                    </PhoneSaveBtn>
                  </PhoneEditRow>
                </PhoneUpdateBlock>
              )}

              <ActionButton onClick={() => navigate(`/clientes/${grupo.cliente_id}`)}>
                <MdPerson style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Ver Cliente
              </ActionButton>
            </AlertCard>
            );
          })}

          {/* ── Próximos a vencer — agrupados por cliente ── */}
          {clientesProximos.length > 0 && hayAmbos && (
            <SectionDivider>
              <SectionDividerLabel>Próximos a vencer — {clientesProximos.length} {clientesProximos.length === 1 ? 'cliente' : 'clientes'}</SectionDividerLabel>
            </SectionDivider>
          )}

          {clientesProximos.map(grupo => {
            const sel = selectedProximos[grupo.cliente_id] ?? new Set(grupo.recibos.map(r => r.recibo_id));
            const numSel = grupo.recibos.filter(r => sel.has(r.recibo_id)).length;
            const allSel = numSel === grupo.recibos.length;
            const waKey = `p_${grupo.cliente_id}`;
            const minDias = grupo.recibos[0].dias;

            return (
            <AlertCard key={`prox-${grupo.cliente_id}`} cardType="upcoming">
              <AlertHeader>
                <AlertIcon type="upcoming"><MdSchedule /></AlertIcon>
                <AlertBadge type="upcoming">
                  {grupo.recibos.length} recibo{grupo.recibos.length !== 1 ? 's' : ''} próximo{grupo.recibos.length !== 1 ? 's' : ''}
                </AlertBadge>
              </AlertHeader>

              <ClientName>{grupo.cliente_nombre || 'Sin nombre'}</ClientName>
              {grupo.cliente_telefono && (
                <ClientPhone><MdPhone />{grupo.cliente_telefono}</ClientPhone>
              )}

              <Amount>
                ${grupo.total_saldo.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Amount>
              <AmountSubtext>
                {minDias === 0 ? 'vence hoy' : `vence en ${minDias} día${minDias !== 1 ? 's' : ''} (más próximo)`}
              </AmountSubtext>

              <ReceiptsList>
                <ReceiptsListHeader>
                  <ReceiptsListMeta>
                    {numSel} de {grupo.recibos.length} seleccionado{numSel !== 1 ? 's' : ''}
                  </ReceiptsListMeta>
                  <ToggleAllBtn onClick={() => toggleAllProximos(grupo)}>
                    {allSel ? 'Quitar todos' : 'Seleccionar todos'}
                  </ToggleAllBtn>
                </ReceiptsListHeader>
                {grupo.recibos.map(r => (
                  <ReceiptListItem key={r.id} onClick={() => toggleReciboProximo(grupo.cliente_id, r.recibo_id)}>
                    <ReceiptCheckbox
                      type="checkbox"
                      checked={sel.has(r.recibo_id)}
                      onChange={() => toggleReciboProximo(grupo.cliente_id, r.recibo_id)}
                      onClick={e => e.stopPropagation()}
                    />
                    <ReceiptListLeft>
                      <ReceiptListNum>
                        {r.subcliente_nombre && `${r.subcliente_nombre} · `}Recibo #{r.numero_recibo}
                      </ReceiptListNum>
                      <ReceiptListDays warning>
                        {r.dias === 0 ? 'Vence hoy' : `Vence en ${r.dias} día${r.dias !== 1 ? 's' : ''}`}
                      </ReceiptListDays>
                    </ReceiptListLeft>
                    <ReceiptListAmount>
                      ${parseFloat(r.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </ReceiptListAmount>
                    <ReceiptListNav
                      onClick={e => { e.stopPropagation(); navigate('/registrar-pago', { state: { reciboId: r.recibo_id, clienteId: grupo.cliente_id } }); }}
                      title="Registrar pago"
                      pay
                    >
                      <MdPayment />
                    </ReceiptListNav>
                    <ReceiptListNav
                      onClick={e => { e.stopPropagation(); navigate(`/recibos/${r.recibo_id}`); }}
                      title="Ver recibo"
                    >
                      <MdOpenInNew />
                    </ReceiptListNav>
                  </ReceiptListItem>
                ))}
              </ReceiptsList>

              {isPhoneValid(grupo.cliente_telefono) ? (
                <NotificationButtons>
                  <WhatsAppButton
                    onClick={() => handleSendWhatsAppProximos(grupo)}
                    disabled={sendingWA[waKey] || numSel === 0}
                    title="Enviar recordatorio de cobro por WhatsApp"
                  >
                    <FaWhatsapp />
                    {sendingWA[waKey]
                      ? 'Enviando...'
                      : numSel < grupo.recibos.length
                        ? `Enviar WhatsApp (${numSel})`
                        : 'Enviar WhatsApp'
                    }
                  </WhatsAppButton>
                  <ConfigButton
                    active={configCache[`${grupo.cliente_id}_proximo`] === true}
                    onClick={() => openConfigModal({ cliente_id: grupo.cliente_id, cliente_nombre: grupo.cliente_nombre, tipo: 'proximo' })}
                    title="Configurar mensajes automáticos para recibos próximos a vencer"
                  >
                    <MdSettings />
                    Auto
                  </ConfigButton>
                </NotificationButtons>
              ) : (
                <PhoneUpdateBlock>
                  <PhoneWarningText>
                    <MdPhone />
                    {phoneStatus(grupo.cliente_telefono) === 'missing'
                      ? 'Sin teléfono registrado'
                      : `Teléfono inválido: "${grupo.cliente_telefono}"`}
                  </PhoneWarningText>
                  <PhoneEditRow>
                    <PhoneInput
                      type="tel"
                      placeholder="Ej. 6641234567"
                      value={editingPhone[grupo.cliente_id] ?? (grupo.cliente_telefono || '')}
                      onChange={e => setEditingPhone(prev => ({ ...prev, [grupo.cliente_id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handleSavePhone(grupo.cliente_id)}
                    />
                    <PhoneSaveBtn
                      onClick={() => handleSavePhone(grupo.cliente_id)}
                      disabled={savingPhone[grupo.cliente_id]}
                    >
                      {savingPhone[grupo.cliente_id] ? 'Guardando...' : 'Guardar'}
                    </PhoneSaveBtn>
                  </PhoneEditRow>
                </PhoneUpdateBlock>
              )}

              <ActionButton onClick={() => navigate(`/clientes/${grupo.cliente_id}`)}>
                <MdPerson style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Ver Cliente
              </ActionButton>
            </AlertCard>
            );
          })}
        </AlertsGrid>
      )}

      {/* Modal de configuración automática por cliente */}
      {configModal && (
        <Overlay onClick={() => setConfigModal(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaWhatsapp style={{ color: WHATSAPP_GREEN, marginRight: '8px' }} />
                Mensajes automáticos
              </ModalTitle>
              <CloseBtn onClick={() => setConfigModal(null)}><MdClose /></CloseBtn>
            </ModalHeader>

            <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
              Cliente: <strong>{configModal.clienteNombre}</strong><br />
              Tipo: <strong>{configModal.tipo === 'proximo' ? 'Próximos a vencer' : 'Vencidos'}</strong>
            </div>

            <ToggleRow>
              <Label style={{ margin: 0 }}>Activar recordatorios automáticos</Label>
              <Toggle
                type="checkbox"
                checked={configData.activo}
                onChange={e => setConfigData(prev => ({ ...prev, activo: e.target.checked }))}
              />
            </ToggleRow>

            <FormGroup>
              <Label>Enviar recordatorio cada (días)</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={configData.frecuencia_dias}
                onChange={e => setConfigData(prev => ({ ...prev, frecuencia_dias: parseInt(e.target.value) || 7 }))}
                disabled={!configData.activo}
              />
              <span style={{ fontSize: '12px', color: '#888' }}>
                El sistema enviará un WhatsApp de cobro cada {configData.frecuencia_dias} día(s) mientras haya recibos pendientes.
              </span>
            </FormGroup>

            <SaveBtn onClick={handleSaveConfig} disabled={savingConfig}>
              <FaWhatsapp />
              {savingConfig ? 'Guardando...' : 'Guardar configuración'}
            </SaveBtn>
          </Modal>
        </Overlay>
      )}

      {/* Modal Auto a todos — por cliente */}
      {autoTodosModal && (() => {
        const grupos = autoTodosModal === 'vencido' ? clientesVencidos : clientesProximos;
        const activosCount = Object.values(clientsAutoConfig).filter(c => c?.activo).length;
        return (
          <Overlay onClick={() => setAutoTodosModal(null)}>
            <WideModal onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  <MdSettings style={{ marginRight: '8px' }} />
                  Mensajes automáticos — {autoTodosModal === 'proximo' ? 'Próximos a vencer' : 'Vencidos'}
                </ModalTitle>
                <CloseBtn onClick={() => setAutoTodosModal(null)}><MdClose /></CloseBtn>
              </ModalHeader>

              {/* Controles masivos */}
              <BulkControls>
                <BulkControlLabel>Todos:</BulkControlLabel>
                <SmallBtn onClick={() => toggleAllAuto(true)}>Activar todos</SmallBtn>
                <SmallBtn onClick={() => toggleAllAuto(false)}>Desactivar todos</SmallBtn>
                <FreqApplyRow>
                  <ClientConfigFreqLabel>Frecuencia:</ClientConfigFreqLabel>
                  <SmallNumberInput
                    type="number" min="1" max="365"
                    value={globalFreq}
                    onChange={e => setGlobalFreq(parseInt(e.target.value) || 7)}
                  />
                  <ClientConfigFreqLabel>días</ClientConfigFreqLabel>
                  <SmallBtn onClick={applyFreqAll}>Aplicar a todos</SmallBtn>
                </FreqApplyRow>
              </BulkControls>

              {/* Lista de clientes */}
              <ClientsList>
                {loadingAutoConfigs ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                    Cargando configuraciones...
                  </div>
                ) : grupos.map(g => {
                  const cfg = clientsAutoConfig[g.cliente_id] || { activo: false, frecuencia_dias: 7 };
                  const validPhone = isPhoneValid(g.cliente_telefono);
                  return (
                    <ClientConfigRow key={g.cliente_id}>
                      <ClientConfigToggle
                        type="checkbox"
                        checked={cfg.activo}
                        onChange={e => updateClientConfig(g.cliente_id, 'activo', e.target.checked)}
                      />
                      <ClientConfigName>
                        <ClientConfigNameText title={g.cliente_nombre}>
                          {g.cliente_nombre}
                        </ClientConfigNameText>
                        <ClientConfigPhone valid={validPhone}>
                          <MdPhone />
                          {validPhone ? g.cliente_telefono : 'Sin teléfono válido'}
                        </ClientConfigPhone>
                      </ClientConfigName>
                      <ClientConfigFreqRow>
                        <ClientConfigFreqInput
                          type="number" min="1" max="365"
                          value={cfg.frecuencia_dias}
                          disabled={!cfg.activo}
                          onChange={e => updateClientConfig(g.cliente_id, 'frecuencia_dias', parseInt(e.target.value) || 7)}
                        />
                        <ClientConfigFreqLabel>días</ClientConfigFreqLabel>
                      </ClientConfigFreqRow>
                    </ClientConfigRow>
                  );
                })}
              </ClientsList>

              <ModalFooter>
                <SaveBtn onClick={handleSaveAutoTodos} disabled={savingAutoTodos} style={{ flex: 1 }}>
                  <MdSettings />
                  {savingAutoTodos
                    ? 'Guardando...'
                    : `Guardar — ${activosCount} activo${activosCount !== 1 ? 's' : ''} de ${grupos.length}`}
                </SaveBtn>
                <SmallBtn onClick={() => setAutoTodosModal(null)} style={{ padding: '8px 20px' }}>
                  Cancelar
                </SmallBtn>
              </ModalFooter>
            </WideModal>
          </Overlay>
        );
      })()}

      {/* Modal de carga al enviar todos */}
      {sendingAll && (
        <LoadingOverlay>
          <LoadingCard>
            <Spinner />
            <LoadingTitle>Enviando mensajes de WhatsApp...</LoadingTitle>
            <ProgressBar>
              <ProgressFill pct={sendingProgress.total > 0 ? (sendingProgress.current / sendingProgress.total) * 100 : 0} />
            </ProgressBar>
            <LoadingProgress>
              {sendingProgress.current} de {sendingProgress.total} cliente{sendingProgress.total !== 1 ? 's' : ''}
            </LoadingProgress>
          </LoadingCard>
        </LoadingOverlay>
      )}

      {/* Toast de notificación */}
      {toast && <Toast error={toast.error}>{toast.message}</Toast>}
    </AlertsContainer>
  );
};

export default Alerts;
